// IndexedDB wrapper for offline data storage
const DB_NAME = 'shelter_offline_db';
const DB_VERSION = 1;

const STORES = [
  'clients',
  'rooms',
  'sites',
  'alerts',
  'notes',
  'service_logs',
  'rosters',
  'roster_entries',
  'documents',
  'profiles',
  'notifications',
  'sync_queue'
] as const;

type StoreName = typeof STORES[number];

let db: IDBDatabase | null = null;

export const initOfflineDb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      
      STORES.forEach(storeName => {
        if (!database.objectStoreNames.contains(storeName)) {
          const store = database.createObjectStore(storeName, { keyPath: 'id' });
          store.createIndex('updated_at', 'updated_at', { unique: false });
          
          if (storeName === 'sync_queue') {
            store.createIndex('created_at', 'created_at', { unique: false });
            store.createIndex('status', 'status', { unique: false });
          }
        }
      });
    };
  });
};

export const saveToOfflineStore = async <T extends { id: string }>(
  storeName: StoreName,
  data: T | T[]
): Promise<void> => {
  const database = await initOfflineDb();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const items = Array.isArray(data) ? data : [data];
    
    items.forEach(item => {
      store.put({ ...item, _cached_at: new Date().toISOString() });
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getFromOfflineStore = async <T>(
  storeName: StoreName
): Promise<T[]> => {
  const database = await initOfflineDb();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
};

export const getByIdFromOfflineStore = async <T>(
  storeName: StoreName,
  id: string
): Promise<T | null> => {
  const database = await initOfflineDb();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result as T | null);
    request.onerror = () => reject(request.error);
  });
};

export const deleteFromOfflineStore = async (
  storeName: StoreName,
  id: string
): Promise<void> => {
  const database = await initOfflineDb();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const clearOfflineStore = async (storeName: StoreName): Promise<void> => {
  const database = await initOfflineDb();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Sync queue operations
export interface SyncQueueItem {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data: any;
  created_at: string;
  status: 'pending' | 'syncing' | 'failed';
  retries: number;
  error?: string;
}

export const addToSyncQueue = async (item: Omit<SyncQueueItem, 'id' | 'created_at' | 'status' | 'retries'>): Promise<string> => {
  const id = crypto.randomUUID();
  const queueItem: SyncQueueItem = {
    ...item,
    id,
    created_at: new Date().toISOString(),
    status: 'pending',
    retries: 0
  };
  
  await saveToOfflineStore('sync_queue', queueItem);
  return id;
};

export const getPendingSyncItems = async (): Promise<SyncQueueItem[]> => {
  const items = await getFromOfflineStore<SyncQueueItem>('sync_queue');
  return items
    .filter(item => item.status === 'pending' || item.status === 'failed')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};

export const updateSyncItemStatus = async (
  id: string,
  status: SyncQueueItem['status'],
  error?: string
): Promise<void> => {
  const item = await getByIdFromOfflineStore<SyncQueueItem>('sync_queue', id);
  if (item) {
    await saveToOfflineStore('sync_queue', {
      ...item,
      status,
      retries: status === 'failed' ? item.retries + 1 : item.retries,
      error
    });
  }
};

export const removeSyncItem = async (id: string): Promise<void> => {
  await deleteFromOfflineStore('sync_queue', id);
};
